import { createAsyncThunk } from "@reduxjs/toolkit";

import { environment } from "../../environments";
import {
  CreateAutomationRequest,
  CreateAutomationResponse,
  DeleteAutomationRequest,
  DeleteAutomationResponse,
  GetAutomationActionTypesRequest,
  GetAutomationActionTypesResponse,
  GetAutomationEntityTypesRequest,
  GetAutomationEntityTypesResponse,
  GetAutomationListRequest,
  GetAutomationListResponse,
  GetAutomationSettingsRequest,
  GetAutomationSettingsResponse,
  GetAutomationTriggerTypeRequest,
  GetAutomationTriggerTypeResponse,
  GetEntityFieldsRequest,
  GetEntityFieldsResponse,
  SetAutomationStateRequest,
  SetAutomationStateResponse,
} from "../../models/Automation.model";

const API_BASE_URL = environment.apiUrl;
export const getAutomationTriggerType = createAsyncThunk<
  GetAutomationTriggerTypeResponse,
  GetAutomationTriggerTypeRequest,
  { rejectValue: string }
>(
  "automation/getAutomationTriggerType",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}Automation/GetAutomationTriggerType`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: GetAutomationTriggerTypeResponse = await response.json();

      if (!data.success)
        return rejectWithValue(data.message || "Failed to fetch base tables");
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

// קבלת ערכים לטבלה ספציפית
export const getAutomationEntityTypes = createAsyncThunk<
  GetAutomationEntityTypesResponse,
  GetAutomationEntityTypesRequest,
  { rejectValue: string }
>(
  "automation/getAutomationEntityTypes",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}Automation/GetAutomationEntityTypes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: GetAutomationEntityTypesResponse = await response.json();

      if (!data.success)
        return rejectWithValue(data.message || "Failed to fetch table values");
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const getAutomationList = createAsyncThunk<
  GetAutomationListResponse,
  GetAutomationListRequest,
  { rejectValue: string }
>("automation/getAutomationList", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}Automation/GetAutomationsList`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: GetAutomationListResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to fetch table values");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getAutomationSettings = createAsyncThunk<
  GetAutomationSettingsResponse,
  GetAutomationSettingsRequest,
  { rejectValue: string }
>(
  "automation/getAutomationSettings",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}Automation/GetAutomationSettings`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: GetAutomationSettingsResponse = await response.json();

      if (!data.success)
        return rejectWithValue(
          data.message || "Failed to fetch automation settings",
        );
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const deleteAutomation = createAsyncThunk<
  DeleteAutomationResponse,
  DeleteAutomationRequest,
  { rejectValue: string }
>("automation/deleteAutomation", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Automation/DeleteAutomation`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: DeleteAutomationResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to delete automation");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getEntityFields = createAsyncThunk<
  GetEntityFieldsResponse,
  GetEntityFieldsRequest,
  { rejectValue: string }
>("automation/getEntityFields", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Automation/GetEntityFields`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: GetEntityFieldsResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to fetch entity fields");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const setAutomationState = createAsyncThunk<
  SetAutomationStateResponse,
  SetAutomationStateRequest,
  { rejectValue: string }
>("automation/setAutomationState", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}Automation/SetAutomationState`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: SetAutomationStateResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to set automation state");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const createAutomation = createAsyncThunk<
  CreateAutomationResponse,
  CreateAutomationRequest,
  { rejectValue: string }
>("automation/createAutomation", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Automation/CreateAutomation`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: CreateAutomationResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to create automation");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getAutomationActionTypes = createAsyncThunk<
  GetAutomationActionTypesResponse,
  GetAutomationActionTypesRequest,
  { rejectValue: string }
>(
  "automation/getAutomationActionTypes",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}Automation/GetAutomationActionTypes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) {
        let serverMessage = "";

        try {
          const errorPayload = await response.json();
          if (errorPayload && typeof errorPayload === "object") {
            const message = (errorPayload as { message?: unknown }).message;
            if (typeof message === "string") {
              serverMessage = message;
            }
          }
        } catch {
          try {
            const text = await response.text();
            serverMessage = text || "";
          } catch {
            serverMessage = "";
          }
        }

        throw new Error(
          serverMessage
            ? `HTTP ${response.status} - ${serverMessage}`
            : `HTTP ${response.status}`,
        );
      }
      const data: GetAutomationActionTypesResponse = await response.json();

      if (!data.success)
        return rejectWithValue(
          data.message || "Failed to fetch automation action types",
        );
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);
